import type { Translation } from '../types';

export const ptBR: Translation = {
  meta: { name: 'Português (Brasil)', english: 'Portuguese (Brazil)', tag: 'pt-BR', dir: 'ltr' },

  common: {
    menu: "Menu",
    cancel: 'Cancelar',
    done: 'Pronto',
    join: 'Entrar',
    leave: 'Sair',
    refresh: 'Atualizar',
    save: 'Salvar',
    you: 'você',
    host: 'anfitrião',
    ai: 'IA',
    ready: 'Pronto',
    notReady: 'Não pronto',
  },

  conn: {
    live: 'Conectado',
    connecting: 'Conectando',
    offline: 'Offline',
    connectingFull: 'Conectando…',
    reconnectBanner: 'Conexão perdida — reconectando. Seu lugar fica guardado por 3 minutos.',
  },

  nickname: {
    pickTitle: 'Escolha um nome',
    changeTitle: 'Mudar seu nome',
    subtitle: 'É assim que os outros jogadores vão te ver.',
    placeholder: 'ex.: Curinga +4',
    label: 'Apelido',
    letsPlay: 'Bora jogar',
  },

  lobby: {
    heroTitle: 'Jogue UNO com amigos',
    heroSubtitle: 'Sem cadastro. Compartilhe um link e distribua.',
    createRoom: 'Criar sala',
    roomCode: 'Código da sala',
    publicRooms: 'Salas públicas',
    empty: 'Ainda não há salas públicas — crie a primeira.',
    cardMeta: 'anfitrião {host} · turnos de {seconds}s',
    stacking: 'acúmulo',
    badgeOpen: 'Aberta',
    badgeFull: 'Cheia',
    badgeLive: 'Em jogo',
    lockedTitle: 'A sala {id} está trancada',
    password: 'Senha',
    passwordLabel: 'Senha da sala',
  },

  create: {
    title: 'Nova sala',
    roomName: 'Nome da sala',
    roomNamePlaceholder: 'UNO de sexta à noite',
    maxPlayers: 'Máx. de jogadores',
    playersOption: { one: '{count} jogador', other: '{count} jogadores' },
    passwordOptional: 'Senha (opcional)',
    passwordPlaceholder: 'Deixe em branco para sala aberta',
    listPublic: 'Mostrar na lista de salas públicas',
    houseRules: 'Regras da casa',
    submit: 'Criar',
  },

  rules: {
    turnTimer: 'Tempo por turno',
    turnTimerHint: 'Quando acaba, um bot joga uma carta válida aleatória por você.',
    startingHand: 'Mão inicial',
    unoPenalty: 'Punição do UNO',
    stackDrawTwo: 'Acumular +2',
    stackDrawTwoHint: 'Responder a um +2 com outro +2 em vez de comprar',
    stackDrawFour: 'Acumular +4',
    stackDrawFourHint: 'Responder a um +4 com outro +4',
    challengeWildFour: 'Contestar o +4',
    challengeWildFourHint: 'O próximo jogador pode contestar um +4 ilegal',
    strictWildFour: '+4 restrito',
    strictWildFourHint: 'Só pode ser jogado se você não tiver carta da cor ativa',
    playDrawn: 'Jogar o que comprar',
    playDrawnHint: 'A carta comprada pode ser jogada na hora',
    drawUntilPlayable: 'Comprar até poder jogar',
    drawUntilPlayableHint: 'Continuar comprando até vir uma carta válida',
    hostOnly: 'só o anfitrião pode alterar',
  },

  room: {
    copyLink: 'Copiar link de convite',
    copied: '✓ Copiado',
    roomLabel: 'Sala',
    locked: 'trancada',
    public: 'pública',
    unlisted: 'não listada',
    readyStatus: '{ready} de {total} prontos',
    botsJoining: { one: '{count} bot entrando', other: '{count} bots entrando' },
    startsWhenReady: 'a partida começa assim que todos estiverem prontos.',
    needMorePlayers: 'São necessários {min} jogadores — convide alguém ou adicione um bot.',
    playersTitle: 'Jogadores',
    botsTitle: 'Bots de IA',
    botsHostOnly: 'só o anfitrião pode alterar',
    noBots: 'Sem bots',
    botsWillJoin: { one: '{count} bot vai entrar', other: '{count} bots vão entrar' },
    botsHint: 'Eles sentam quando a partida começa. Cabem até {max} agora.',
    botsRoomFull: 'A sala está cheia — sem espaço para bots.',
    soloCta: 'Jogando sozinho? Adicione um bot e comece',
    addBot: 'Adicionar bot',
    removeBot: 'Remover bot',
    removePlayer: 'Remover {name}',
    botPlaceholder: 'Bot {n}',
    defaultRoomName: 'Sala de {name}',
    imReady: 'Estou pronto',
    cancelReady: 'Cancelar pronto',
    offlineTag: 'offline',
  },

  game: {
    yourTurn: 'Sua vez',
    yourTurnStack: 'Sua vez — acumule ou compre {count}',
    othersTurn: 'Vez de {name}',
    waiting: 'Aguardando…',
    gameOver: 'Fim de jogo',
    challengeWindow: 'Curinga +4 — janela de contestação',
    drawPileLeft: 'restam {count}',
    discard: 'descarte',
    draw: 'Comprar',
    takeCards: 'Comprar {count}',
    pass: 'Passar',
    uno: 'UNO!',
    judge: 'DENUNCIAR',
    cardCount: { one: '{count} carta', other: '{count} cartas' },
    clockwise: 'Sentido horário',
    counterClockwise: 'Sentido anti-horário',
    activeColour: 'Cor ativa',
    noCards: 'Sem cartas — mandou bem.',
    oneCard: 'uma carta!',
    unoTag: 'UNO',
    forfeitedTag: 'desistiu',
    offlineFor: 'offline {seconds}s',
    placeTag: '{place}º',
    pickColour: 'Escolha uma cor',
    challengeTitle: '{name} jogou um Curinga +4. Contestar?',
    challengeExplain: 'Se estava blefando, essa pessoa compra {bluff}. Se não, você compra {fail}.',
    challengeBtn: 'Contestar',
    acceptBtn: 'Comprar {count}',
    youWin: '🎉 Você venceu!',
    standingsTitle: 'Fim de jogo',
    points: '{points} pts',
    forfeited: 'desistiu',
    backToLobby: 'Voltar ao saguão',
    playAgain: 'Jogar de novo',
    waitingOthers: 'Aguardando os outros…',
    faceDownCard: 'Carta virada para baixo',
    sortByColour: "Ordenar por cor",
    sortAsDealt: "Manter a ordem distribuída",
    drawACard: 'Comprar uma carta',
    drawNCards: 'Comprar {count} cartas',
  },

  events: {
    someone: 'Alguém',
    unoCalledYou: 'Você gritou UNO!',
    unoCalledOther: '{name} gritou UNO!',
    judged: '{accuser} pegou {target} — +{count} cartas',
    judgedYou: '{accuser} te pegou — +{count} cartas',
    challengeBluff: 'Blefe descoberto — {name} compra {count}',
    challengeFailed: 'Contestação falhou — {name} compra {count}',
    forfeited: '{name} desistiu.',
    afkYou: 'Seu tempo acabou — jogada automática.',
    afkOther: 'O tempo de {name} acabou — jogada automática.',
    hostChanged: '{name} agora é o anfitrião.',
    reshuffled: 'Monte de compra reembaralhado.',
    youWin: 'Você venceu!',
    winner: '{name} venceu!',
    gameOver: 'Fim de jogo.',
  },

  errors: {
    bad_request: 'Algo deu errado.',
    no_session: 'Sessão expirada — reconectando.',
    room_not_found: 'Essa sala não existe.',
    room_full: 'Essa sala está cheia.',
    wrong_password: 'Senha incorreta.',
    already_in_room: 'Saia primeiro da sala atual.',
    not_in_room: 'Você não está em nenhuma sala.',
    not_host: 'Só o anfitrião pode fazer isso.',
    game_in_progress: 'Essa partida já começou.',
    not_enough_players: 'Jogadores insuficientes.',
    stale_action: 'Alguém agiu antes — sua jogada foi descartada.',
    illegal_action: 'Essa jogada não é permitida.',
    rate_limited: 'Vai com calma.',
    nickname_invalid: 'Esse apelido não serve.',
  },

  theme: {
    title: 'Tema das cartas',
    description: 'Imagens ausentes voltam para o baralho CSS embutido.',
    builtIn: 'Embutido — não precisa de arquivos',
    pngHint: 'Coloque PNGs 300×450 em {path} e registre no índice de temas.',
  },

  language: {
    title: 'Idioma',
    description: 'Vale na hora e fica salvo neste dispositivo.',
  },

  cards: {
    red: "vermelho",
    yellow: "amarelo",
    green: "verde",
    blue: "azul",
    wild: "curinga",
    skip: "Pula",
    reverse: "Inverte",
    draw2: "+2",
    wildCard: "Curinga",
    wild4: "+4",
  },

  log: {
    title: "Histórico",
    empty: "Nada ainda.",
    close: "Ocultar histórico",
    timestamps: "Horários",
    chatPlaceholder: "Diga algo…",
    send: "Enviar",
    played: "{name} jogou {card}",
    drew: "{name} comprou {count}",
    skipped: "{name} perdeu a vez",
    reversed: "Sentido invertido",
    joined: "{name} entrou",
    left: "{name} saiu",
    started: "Partida iniciada",
  },

  profile: {
    avatar: "Avatar",
    avatarHint: "Escolha um avatar, ou mantenha sua inicial.",
  },

  leave: {
    title: "Sair da partida?",
    body: "A rodada ainda está rolando. Se sair, você desiste e suas cartas voltam para o monte.",
    stay: "Continuar jogando",
    confirm: "Sair mesmo assim",
    unload: "Você está no meio de uma partida.",
  },
};
